import React, { Component } from 'react'
import compose from 'lodash/fp/compose'
import cn from 'classnames'
import noop from 'lodash/noop'
import SVGInline from 'react-svg-inline'

import './shelf-info.styl'

import uiBox, { DecoratorProps } from 'client/shared/decorators/ui-box'
import deviceHelper from 'shared/tools/device-helper'

import { ShelfCreator } from 'client/bookmate/blocks/shelf-creator'
import Spacer from 'client/shared/blocks/spacer'
import LockIcon from 'client/shared/icons/lock.svg'

import { ShelfProps } from 'client/shared/types/shelf'
import { State as AppState } from 'client/shared/reducers/app-reducer'

const MOVE_POSITION_SPEED = 2

type Props = {
  shelf: ShelfProps
  renderButton: () => React.ReactHTMLElement<HTMLDivElement>
  app: AppState
} & DecoratorProps

class _ShelfInfo extends Component<Props> {
  ticking: boolean
  lastScrollY: number
  onScroll: () => void

  constructor(props) {
    super(props)

    this.ticking = false
    this.lastScrollY = 0
    this.onScroll = noop
  }

  moveCoverPosition = (): void => {
    this.ticking = false

    const { cover } = this.refs
    const { bottom, height } = cover.getBoundingClientRect()

    if (bottom > 0) {
      cover.style.backgroundPosition = `50% ${Math.min(
        (this.lastScrollY / height) * MOVE_POSITION_SPEED * 100,
        100,
      )}%`
      cover.style.willChange = 'transform'
    } else {
      cover.style.backgroundPosition = ''
      cover.style.willChange = ''
    }
  }

  componentDidMount() {
    if (deviceHelper.isMobileSize(this.props.app)) return

    window.addEventListener(
      'scroll',
      (this.onScroll = () => {
        this.lastScrollY = Math.max(window.pageYOffset, 0)
        if (!this.ticking) {
          window.requestAnimationFrame(this.moveCoverPosition)
        }
        this.ticking = true
      }),
    )
  }

  componentWillUnmount(): void {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll)
    }
  }

  render(): JSX.Element | null {
    if (!this.props.shelf) return null

    const {
      shelf,
      shelf: { title, cover },
      renderButton,
      isMobileSize,
      app,
    } = this.props

    const titleClasses = cn('shelf-info__title', {
      'shelf-info__title_without-spaces': !/\s/.test(title.trim()),
      'shelf-info__title_scaled': title.length > 80,
    })
    const backgroundImage = cover.large ? `url(${cover.large})` : null

    return (
      <div className="shelf-info">
        <div className="shelf-info__buttons">
          {!isMobileSize() && renderButton()}
          <Spacer
            size={{
              desktopLarge: 64,
              desktopMedium: 64,
              desktop: 64,
              tablet: 16,
              mobile: 16,
            }}
          />
        </div>
        <div className="shelf-info__cover">
          <div className="shelf-info__title-wrapper">
            {shelf.state === 'hidden' && (
              <SVGInline svg={LockIcon} width="34px" height="34px" />
            )}
            <h1 className={titleClasses}>{title}</h1>
          </div>
          <div className="shelf-info__overlay" />
          <span className="shelf-info__creator">
            <ShelfCreator shelf={shelf} query={app.storedQuery} />
          </span>
          <div
            className="shelf-info__image"
            style={{ backgroundImage }}
            ref="cover"
          />
        </div>
      </div>
    )
  }
}

const wrappers = compose(uiBox)

export const ShelfInfo = wrappers(_ShelfInfo)
